import { AxiosResponse, AxiosError } from 'axios';
import { instance } from 'helpers/http';
import StringUtils from 'helpers/utils';
import { DeepPartial } from 'helpers/utils/type';

const headers = {
  'Content-Type': 'application/json',
  'partner-id': process.env.REACT_APP_PARTNER_ID
};

// Define the SubmissionService object that exports two async functions
const SubmissionService = {
  // Function to send an HTTP POST request to /step-1 endpoint
  submitStep1: async (data: any) => {
    return await instance.post('/v1/ibb/submissions/step1', data, {
      headers: headers
    });
  },
  // Function to send an HTTP POST or PATCH request to /v1/submissions endpoint
  submitStep2Section1: async ({
    entity
  }: RequestSubmissionEntity): Promise<AxiosResponse<ResponseSubmission> | undefined> => {
    try {
      // Generate a unique UUID to use as reference_id
      const reference_id = StringUtils.generateUUID();

      // Send an HTTP POST request to /v1/submissions endpoint with the entity and reference_id in the payload
      const responseCreateSubmissions = await instance.post<ResponseSubmission>(
        '/v1/ibb/submissions/step2',
        {
          entity: {
            npwp: entity.npwp,
            izin_usaha: {
              number: entity.izin_usaha.number,
              type: entity.izin_usaha.type
            }
          },
          reference_id
        },
        {
          headers: headers
        }
      );

      // If the response route is 'EXISTING', return a 302 status code with a path to /exists
      if (responseCreateSubmissions?.data?.route === 'EXISTING') {
        return {
          ...responseCreateSubmissions,
          status: 302,
          data: {
            ...responseCreateSubmissions.data,
            redirect_url: '/exists'
          }
        };
      }

      if (responseCreateSubmissions) {
        return responseCreateSubmissions;
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ error_code: string; message: string }>;

      if (axiosError.response?.status === 422 && axiosError.response?.data.error_code === 'OFFLINE_EXISTING_CUSTOMER') {
        return {
          ...axiosError.response,
          status: 302,
          data: {
            id: '',
            reference_id: '',
            route: 'EXISTING',
            status: '',
            created_at: '',
            redirect_url: '/exists'
          }
        };
      }

      throw error;
    }
  },
  submitStep3: async ({ id, data }: { id: number | string; data: RequestSubmissionPayload }) => {
    return await instance.patch(`/v1/ibb/submissions/step2/${id}`, data, {
      headers: headers
    });
  },
  submitAllForm: async ({ id }: { id: string }) => {
    return await instance.post(
      '/v1/ibb/submissions/submit',
      {},
      {
        headers: {
          ...headers,
          'submission-id': id
        }
      }
    );
  },
  reconfirmationSuccess: async (
    id: string,
    { email, signature, nonce }: { email: string; signature: string; nonce: string }
  ) => {
    return await instance.post<ResponseReview>(
      `/v1/ibb/submissions/${id}/review`,
      {
        status: 'CUSTOMER_CONFIRMED',
        remarks: '',
        email
      },
      {
        params: {
          signature,
          nonce
        }
      }
    );
  },
  reconfirmationReject: async (id: string, email: string) => {
    return await instance.post<ResponseReview>(
      `/v1/ibb/submissions/${id}/review`,
      {
        status: 'CUSTOMER_DISAGREE',
        remarks: 'Skipped by DMO',
        email
      },
      {
        params: {
          skip: true
        }
      }
    );
  }
};

// Define an interface for the response object of the POST request to /v1/submissions
interface ResponseSubmission {
  id: string;
  reference_id: string;
  route: 'NEW' | 'EXISTING';
  status: string;
  created_at: string;
  redirect_url?: string;
}

// Define an interface for the response object of the POST request to /v1/submissions/review
interface ResponseReview {
  data: ResponseSubmission & {
    updated_at: string;
    assigned_dmo: string;
  };
  review: {
    status: string;
    decided_at: string;
  };
}

// Define an interface for the RequestSubmissionPayload that is used in the submitStep2Section1 function
export interface RequestSubmissionPayload {
  entity: DeepPartial<EntityPayload>;
  surat_kuasa?: DeepPartial<AuthorizedSignaturePayload>;
}

export interface RequestSubmissionEntity {
  entity: {
    npwp: string;
    izin_usaha: {
      number: string;
      type: string;
    };
  };
}

// Define an interface for the EntityPayload that is used in the RequestSubmissionPayload interface
interface EntityPayload {
  name: string;
  type: {
    type: string;
    description: string;
  };
  first_akta_pendirian_number: string;
  address: {
    street_address: string;
    rt: string;
    rw: string;
    province: string;
    city: string;
    district: string;
    village: string;
    postal_code: string;
    country: string;
    dati2: string;
  };
  phone_number: string;
  phone_ext: string;
  fax: string;
  mobile_number: string;
  account_purpose: {
    purpose: string;
    description: string;
  };
  document_ids: DocumentIds;
  estimated_credit_amount: string | number;
  estimated_debit_amount: string | number;
  estimated_credit_frequency: string | number;
  estimated_debit_frequency: string | number;
  primary_income_source: {
    type: string;
    description: string;
  };
  business_type: string;
  nationality: string;
  number_of_employees: string | number;
  entity_assets: string | number;
  managements: {
    name: string;
    position: string;
    email_address: string;
    phone_number: string;
    identity_document_id: string;
    address?: string;
    gender?: string;
    identity_number: string;
  }[];
  beneficial_owner: {
    name: string;
    phone_number: string;
    gender: string;
    marital_status: string;
    address: {
      street_address: string;
      rt: string;
      rw: string;
      province: string;
      city: string;
      district: string;
      village: string;
      postal_code: string;
    };
    domicile_address: {
      street_address: string;
      rt: string;
      rw: string;
      province: string;
      city: string;
      district: string;
      village: string;
      postal_code: string;
    };
    identity_document_id: string;
    citizenship: string;
    npwp: string;
    profession: string;
    income_source: string;
    monthly_income: string | number;
    document_ids: string[];
    relationship: string;
  };
  npwp: string;
  izin_usaha: {
    type: string;
    number: string;
  };
  monthly_income: string | number;
}

interface DocumentIds {
  npwp: string;
  ad_arts: string[];
  nib: string;
  siup: string;
  tdp: string;
  skdp: string;
  situ: string;
  sk_menkumham: string;
  laporan_keuangan: string;
  bukti_pendaftaran_perkumpulan: string;
  persetujuan_bendahara_umum_negara: string;
  other_supporting_documents: string[];
  ditjen_ahu: string[];
}

interface AuthorizedSignaturePayload {
  management_authoriser_identity_numbers: string[];
  authorised_signatories: {
    identity_number: string;
    date_of_birth: string;
    place_of_birth: string;
    name: string;
    mobile_number: string;
    email_address: string;
    identity_document_id: string;
    gender: string;
    address: {
      street_address: string;
      rt: string;
      rw: string;
      province: string;
      city: string;
      district: string;
      village: string;
      country: string;
    };
    marital_status: string;
    profession: string;
    relationship: string;
    powers: string[];
  }[];
  terms: string;
}

export default SubmissionService;
